import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LlmManagerCard — Settings → System. Real OpenRouter account credit
 * balance, the live-priced model cascade board (the SAME filtered/sorted
 * list the backend actually picks from — not a separate invented one), and
 * per-model bubbles showing real cost/reliability this session.
 *
 * Polls /api/llm/manager every 30s.
 */

function fmtUsd(n, digits = 2) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(digits)}`;
}

function shortModelName(id) {
  if (!id) return "—";
  const noFree = id.replace(/:free$/, "");
  const parts = noFree.split("/");
  return parts[parts.length - 1];
}

function ModelBubble({ m }) {
  const unhealthy = m.strikes > 0 || m.onCooldown;
  const border = m.isLastWorking
    ? "1px solid rgba(74,222,128,0.55)"
    : unhealthy
      ? "1px solid rgba(248,113,113,0.35)"
      : "1px solid rgba(255,255,255,0.08)";
  const bg = m.isLastWorking
    ? "rgba(74,222,128,0.08)"
    : unhealthy
      ? "rgba(248,113,113,0.05)"
      : "rgba(255,255,255,0.025)";
  const priceLabel = m.isFree
    ? "FREE"
    : `${fmtUsd(m.promptPricePerMTok, 3)}/Mtok in`;

  return (
    <div
      title={m.id}
      style={{
        border, background: bg, borderRadius: 12, padding: "10px 12px",
        display: "flex", flexDirection: "column", gap: 4, minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{
          fontSize: 12, fontWeight: 700, color: "var(--text)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{shortModelName(m.id)}</span>
        {m.isLastWorking && (
          <span style={{
            fontSize: 8, fontWeight: 800, letterSpacing: 0.5, color: "#4ade80",
            background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.32)",
            borderRadius: 4, padding: "1px 5px", flex: "0 0 auto",
          }}>ACTIVE</span>
        )}
      </div>
      <div style={{
        fontSize: 11, fontWeight: 700,
        color: m.isFree ? "#60a5fa" : "#fbbf24",
      }}>{priceLabel}</div>
      <div style={{ fontSize: 10, color: "var(--dim)" }}>
        {m.callsThisSession} call{m.callsThisSession === 1 ? "" : "s"} · {fmtUsd(m.costThisSessionUsd, 4)} this session
      </div>
      {unhealthy && (
        <div style={{ fontSize: 9, fontWeight: 700, color: "#f87171" }}>
          {m.strikes > 0 ? `${m.strikes} recent failure${m.strikes === 1 ? "" : "s"}` : "cooling down"}
        </div>
      )}
    </div>
  );
}

export default function LlmManagerCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/llm/manager`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const account = data?.account;
  const credits = account?.credits;
  const models = data?.models || [];
  const shown = showAll ? models : models.slice(0, 12);
  const breakerOpen = data?.breaker?.open;

  return (
    <div className="card" data-testid="llm-manager-card" style={{
      borderColor: "rgba(167,139,250,0.26)",
      background: "linear-gradient(165deg, rgba(167,139,250,0.04), rgba(96,165,250,0.02))",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 2, fontWeight: 700 }}>LLM MANAGER</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
            {loading ? "Loading…" : `${models.length} model${models.length === 1 ? "" : "s"} in the live cascade`}
          </div>
        </div>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
          padding: "5px 11px", borderRadius: 999,
          color: breakerOpen ? "#f87171" : "#4ade80",
          background: breakerOpen ? "rgba(248,113,113,0.12)" : "rgba(74,222,128,0.12)",
          border: breakerOpen ? "1px solid rgba(248,113,113,0.36)" : "1px solid rgba(74,222,128,0.36)",
        }}>{breakerOpen ? "BREAKER OPEN" : "HEALTHY"}</div>
      </div>

      {/* Real account credit balance — from OpenRouter's admin API */}
      {credits ? (
        <div style={{
          display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
          padding: "10px 12px", borderRadius: 10, marginBottom: 12,
          background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
        }}>
          <div>
            <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 1 }}>ACCOUNT CREDITS</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text)" }}>{fmtUsd(credits.remaining)} <span style={{ fontSize: 10, fontWeight: 500, color: "var(--dim)" }}>remaining</span></div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 1 }}>TOTAL PURCHASED</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{fmtUsd(credits.total_credits)}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 1 }}>USED (ALL-TIME)</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{fmtUsd(credits.total_usage)}</div>
          </div>
          {account?.active_key && (
            <div>
              <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 1 }}>ACTIVE KEY CAP</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                {fmtUsd(account.active_key.limit_remaining, 2)} / {fmtUsd(account.active_key.limit, 0)}
              </div>
            </div>
          )}
        </div>
      ) : account?.error ? (
        <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 12 }}>
          Account credit visibility unavailable ({account.error}) — set OPENROUTER_MANAGEMENT_KEY to enable.
        </div>
      ) : null}

      {/* This session's spend */}
      {data?.totals && (
        <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: data?.marketSession?.session ? 4 : 12 }}>
          This session: {data.totals.calls} call{data.totals.calls === 1 ? "" : "s"}, {data.totals.success} succeeded, {data.totals.errors} failed — {fmtUsd(data.totals.costUsdThisSession, 4)} spent
        </div>
      )}

      {/* Off-hours-aware ambient-chat cost throttle — set by floor_chat.py's
          scene loop each cycle, so this reflects the real, currently-active
          session bucket and cap, not a guess */}
      {data?.marketSession?.session && (
        <div style={{ fontSize: 11, color: data.marketSession.cost_throttle_active ? "var(--warn, #f59e0b)" : "var(--dim)", marginBottom: 12 }}>
          Market session: {data.marketSession.session === "open" ? "open" : "closed"} — ambient chat cost throttle {data.marketSession.cost_throttle_active ? "ACTIVE (cadence slowed)" : "not active"}
        </div>
      )}

      {/* Model bubbles — sorted (reliability, price), same as the real cascade */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8,
      }}>
        {shown.map(m => <ModelBubble key={m.id} m={m} />)}
      </div>

      {models.length > 12 && (
        <button
          onClick={() => setShowAll(s => !s)}
          style={{
            marginTop: 10, padding: "6px 12px", borderRadius: 999,
            border: "1px solid rgba(167,139,250,0.4)", background: "rgba(167,139,250,0.08)",
            color: "#a78bfa", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}
        >{showAll ? "Show fewer" : `Show all ${models.length} models`}</button>
      )}

      <div style={{ marginTop: 10, fontSize: 10, color: "var(--dim)", textAlign: "center", letterSpacing: 0.5 }}>
        sorted by reliability then price — a model that's failed this session drops behind clean candidates regardless of cost · auto-refreshes every 30s
      </div>
    </div>
  );
}
